import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import React, { forwardRef } from 'react';

import { cn } from '../../../utils';

export type FontFamily = 'inter' | 'polysans';
export type FontSize =
  | '5xl'
  | '4xl'
  | '3xl'
  | '2xl'
  | 'xl'
  | 'lg'
  | 'base'
  | 'sm'
  | 'xs';
export type FontWeight = 'regular' | 'semibold' | 'bold';

export interface TextProps {
  className?: string;
  asChild?: boolean;
  fontWeight?: FontWeight;
  fontSize?: FontSize;
  fontFamily?: FontFamily;
  children?: React.ReactElement | JSX.Element;
}

const textVariants = cva('', {
  variants: {
    fontFamily: {
      inter: 'font-inter',
      polysans: 'font-polysans',
    },
    fontSize: {
      '5xl':
        'text-5xl-mobile leading-[var(--text-5xl--line-height-mobile)] md:text-5xl-tablet md:leading-[var(--text-5xl--line-height-tablet)] lg:text-5xl-ds lg:leading-[var(--text-5xl-ds--line-height)]',
      '4xl':
        ' text-4xl-mobile leading-[var(--text-4xl--line-height-mobile)] md:text-4xl-tablet md:leading-[var(--text-4xl--line-height-tablet)] lg:text-4xl lg:leading-[var(--text-4xl--line-height)]',
      '3xl':
        'text-3xl-mobile leading-[var(--text-3xl--line-height-mobile)] md:text-3xl-tablet md:leading-[var(--text-3xl--line-height-tablet)] lg:text-3xl-ds lg:leading-[var(--text-3xl-ds--line-height)]',
      '2xl':
        ' text-2xl-mobile leading-[var(--text-2xl--line-height-mobile)] md:text-2xl-tablet md:leading-[var(--text-2xl--line-height-tablet)] lg:text-2xl lg:leading-[var(--text-2xl--line-height)]',
      xl: 'text-xl-mobile leading-[var(--text-xl--line-height-mobile)] md:text-xl-tablet md:leading-[var(--text-xl--line-height-tablet)] lg:text-xl lg:leading-[var(--text-xl--line-height)]',
      lg: 'text-lg-mobile leading-[var(--text-lg--line-height-mobile)] md:text-lg-tablet md:leading-[var(--text-lg--line-height-tablet)] lg:text-lg lg:leading-[var(--text-lg--line-height)]',
      base: 'text-base-mobile leading-[var(--text-base--line-height-mobile)] md:text-base-tablet md:leading-[var(--text-base--line-height-tablet)] lg:text-base lg:leading-[var(--text-base--line-height)]',
      sm: 'text-sm-mobile leading-[var(--text-sm--line-height-mobile)] md:text-sm-tablet md:leading-[var(--text-sm--line-height-tablet)] lg:text-sm lg:leading-[var(--text-sm--line-height)]',
      xs: 'text-xs-mobile leading-[var(--text-xs--line-height-mobile)] md:text-xs-tablet md:leading-[var(--text-xs--line-height-tablet)] lg:text-xs lg:leading-[var(--text-xs--line-height)]',
    },
    fontWeight: {
      regular: '',
      semibold: 'font-semibold',
      bold: '',
    },
  },
  compoundVariants: [
    // bold and regular are only applied to certain font-sizes and font-families
    {
      fontFamily: 'inter',
      fontSize: ['lg', 'base', 'sm', 'xs'],
      fontWeight: 'bold',
      class: 'font-bold',
    },
    {
      fontFamily: ['inter', 'polysans'],
      fontSize: ['2xl', 'xl', 'lg', 'base', 'sm', 'xs'],
      fontWeight: 'regular',
      class: 'font-normal',
    },
  ],
  defaultVariants: {
    fontFamily: 'inter',
    fontSize: 'base',
    fontWeight: 'semibold',
  },
});

const Text = forwardRef<HTMLParagraphElement, TextProps>(
  (
    {
      className,
      children,
      asChild = false,
      fontWeight = 'semibold',
      fontSize = 'base',
      fontFamily = 'inter',
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'p';

    return (
      <Comp
        ref={ref}
        className={cn(
          textVariants({ fontFamily, fontSize, fontWeight, className })
        )}
      >
        {children}
      </Comp>
    );
  }
);

export { Text };
Text.displayName = 'Text';
